import React from 'react';
import styles from './index.module.less';
import FourViewDisplay from './components/FourViewDisplay';
import SceneViewDisplay from './components/SceneViewDisplay';
import AccessoryCarousel from './components/AccessoryCarousel/index';
import MasterVideo from './components/MasterVideo';
import PanoramicVideo from './components/PanoramicVideo';
import { useEffect, useMemo, useRef, useState } from 'react';
import MobxWrap from '@/components/MobxWrap';
import { useSize } from 'ahooks';
import { ClassNames, isEmpty } from '@/utils';
import { ConfigProvider, useInitConfig } from './useGetConfig';
import { LoadingOutlined } from '@ant-design/icons';
import ProductTracker from '@/utils/Tracker/_product';

// import { ICON_X4 } from './icon';
import { ICON_X4, ICON_Accessory, ICON_360, ICON_Video } from './icon';
import { PointType } from './typings';
import ComponentsErrorBoundary from '@/components/ErrorBoundary/ComponentsErrorBoundary';

/**模块切换器按钮间隔 */
const MOUDLE_BTN_GAP: number = 4;

type BProps = {
  /** 模块数据 */
  props?: Record<string, unknown>;
  /** 按钮key —— 用于开发自知和key的使用 */
  name: string;
  /** 按钮文案 */
  text: string;
  /** 是否符合展示条件 */
  enabled: boolean;
  /** 模块组件  */
  component: React.ComponentType;
  /** 模块按钮图标 */
  icon: React.ReactNode;
  /** 模块按钮埋点上报名称 */
  track_action: string;
};

/** 全景主图模块 */
const PanoramicDisplay = () => {
  const [modules, setModules] = useState<BProps[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const btnRef = useRef<(HTMLDivElement | null)[]>([]);
  const btnWrapRef = useRef<HTMLDivElement | null>(null);
  const [skipIndex, setSkipIndex] = useState<number | null>(null);

  const size = useSize(btnWrapRef);

  const initValue = useInitConfig();
  const { curSelectCommodityId, productDetail, curSelectCommodity } = initValue.mobxStore.product;
  const { LANG } = initValue.mobxStore.area;
  const { isMobile } = initValue.mobxStore.app;

  // 模块切换器按钮的偏移宽度信息
  const btnMap = useMemo(() => {
    if (!btnRef.current) return [];
    return btnRef.current.map((ele, index, _eleBtns) => {
      if (!ele) return { btnWidth: 0, offsetLeft: 0 }; // 处理 ele 为 null 的情况
      const offsetLeft =
        index > 0
          ? [..._eleBtns.slice(0, index)].reduce(
              (acc, cur) => acc + (cur?.getBoundingClientRect()?.width || 0) + MOUDLE_BTN_GAP,
              0
            )
          : 0;
      const btnWidth = ele.getBoundingClientRect()?.width || 0;
      return {
        btnWidth,
        offsetLeft,
      };
    });
  }, [size?.width]);

  useEffect(() => {
    let defaultModules = [
      {
        name: 'fourview',
        text: LANG['productNew.Display.fourviewBtn'],
        component: FourViewDisplay,
        enabled: initValue?.isProductStandardCid && !isEmpty(initValue.fourviewConfig),
        icon: ICON_X4,
        track_action: 'click_fourangle_button',
      },
      {
        name: 'scene',
        text: LANG['productNew.Display.sceneBtn'],
        component: SceneViewDisplay,
        enabled: !initValue?.isProductStandardCid && !isEmpty(initValue.sceneviewConfig),
        icon: ICON_X4,
        track_action: 'click_scene_button',
      },
      {
        name: 'accessoryCarousel',
        text: LANG['productNew.Display.accessoryBtn'],
        component: AccessoryCarousel,
        enabled: !isEmpty(initValue.commodityAccessoryList) && initValue.commodityAccessoryList?.length > 0,
        icon: ICON_Accessory,
        track_action: 'click_item_button',
      },
      {
        name: 'panoramicVideo',
        text: LANG['productNew.Display.panoramicBtn'],
        component: PanoramicVideo,
        enabled: !isEmpty(initValue.panoramicVideo),
        icon: ICON_360,
        track_action: 'click_360_button',
      },
      {
        name: 'masterVideo',
        text: LANG['productNew.Display.videoBtn'],
        component: MasterVideo,
        enabled: initValue?.isProductStandardCid && !isEmpty(initValue.masterVideoList),
        icon: ICON_Video,
        track_action: 'click_video2_button',
      },
    ] as typeof modules;

    // 根据条件过滤模块
    defaultModules = defaultModules.filter((item) => item.enabled);

    // 给scene模块传递切换到配件模块的方法
    const accessoryIndex = defaultModules.findIndex((item) => item.name === 'accessoryCarousel');
    defaultModules.forEach((item) => {
      if (item.name === 'scene') {
        item.props = {
          changeModule: (point: PointType) => {
            setActiveIndex(accessoryIndex);
            setSkipIndex(isMobile ? point?.relateAccessoryIndex : point?.relateAccessoryIndex + 1);
          },
        };
      }
    });
    setModules(defaultModules); //初始话套餐的主图模块
    setActiveIndex(0); //默认选中第一个模块
  }, [curSelectCommodityId]);

  return (
    <ConfigProvider value={{ ...initValue, skipIndex }}>
      <section className={styles.panoraimcSec}>
        <div className={styles.panoraimcWrapper}>
          <div className={styles.loadingIconWrap}>
            <LoadingOutlined className={styles.loadingIcon} style={{ fontSize: '32px' }} />
          </div>

          <ComponentsErrorBoundary name={`【全景】${modules[0]?.name}`} at={['ccb']}>
            <div className={styles.wrap} style={{ display: activeIndex === 0 ? 'block' : 'none' }}>
              {modules[0]?.component && React.createElement(modules[0].component, modules[0]?.props || {})}
            </div>
          </ComponentsErrorBoundary>

          {modules.slice(1).map((item, index) => {
            if (index + 1 !== activeIndex) return null;
            return (
              <ComponentsErrorBoundary key={item.name} name={`【全景】${item.name}`}>
                <item.component key={item.name} {...item.props} />
              </ComponentsErrorBoundary>
            );
          })}
        </div>

        <div className={styles.switchBtnWrap}>
          <div className={styles.btnWrap} ref={btnWrapRef}>
            <div
              className={styles.switchBtnActiveBg}
              style={{
                width: btnMap[activeIndex]?.btnWidth,
                left: btnMap[activeIndex]?.offsetLeft,
              }}
            />
            {modules.map((item, index) => {
              return (
                <div
                  key={index}
                  data-isactive={activeIndex === index}
                  className={ClassNames([styles.switchBtn, activeIndex === index ? styles.active : ''])}
                  onClick={() => {
                    if (typeof skipIndex === 'number') setSkipIndex(null);
                    setActiveIndex(index);
                    ProductTracker.productPageImages({
                      action: item.track_action,
                      category: 'product_image',
                      data: {
                        url_key: productDetail?.url_key,
                        name: curSelectCommodity?.name,
                        id: curSelectCommodityId,
                      },
                    });
                  }}
                  ref={(ref) => (btnRef.current[index] = ref)}
                >
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </ConfigProvider>
  );
};

export default MobxWrap(['app', 'area', 'product'], PanoramicDisplay);
